'use client';

import { useCallback, useEffect, useState } from 'react';
import { colorForIndex } from '@/lib/colors';
import { getDb } from '@/lib/db';
import { STORAGE_UNAVAILABLE_MESSAGE, storageErrorMessage } from '@/lib/storageErrors';
import { Category } from '@/lib/types';

export type CategoryPayload = Omit<Category, 'id' | 'createdAt' | 'updatedAt'>;

function notifyUpdate() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('budget-db-update'));
  }
}

function createId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const list = await db.categories.orderBy('name').toArray();
      setCategories(list);
      setError(null);
    } catch (err) {
      setError(storageErrorMessage('Impossible de charger les catégories.', err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener('budget-db-update', handler);
    return () => window.removeEventListener('budget-db-update', handler);
  }, [load]);

  const addCategory = useCallback(async (payload: CategoryPayload) => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }

    try {
      const now = new Date().toISOString();
      const categoryCount = await db.categories.count();
      await db.categories.add({
        ...payload,
        id: createId(),
        color: payload.color ?? colorForIndex(categoryCount),
        createdAt: now,
        updatedAt: now,
      });
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage("Impossible d'ajouter la catégorie.", err));
    }
  }, []);

  const updateCategory = useCallback(async (id: string, payload: CategoryPayload) => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }

    try {
      await db.categories.update(id, {
        ...payload,
        updatedAt: new Date().toISOString(),
      });
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage('Impossible de modifier la catégorie.', err));
    }
  }, []);

  const deleteCategory = useCallback(async (id: string) => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }

    try {
      const now = new Date().toISOString();
      const tags = await db.tags.where('categoryId').equals(id).toArray();
      const tagIds = tags.map((tag) => tag.id);

      await db.transaction('rw', [db.categories, db.tags, db.transactions, db.recurringExpenses], async () => {
        await db.transactions.where('categoryId').equals(id).modify((transaction) => {
          transaction.categoryId = undefined;
          transaction.updatedAt = now;
        });

        if (tagIds.length > 0) {
          await db.transactions
            .filter((transaction) => transaction.tagIds.some((tagId) => tagIds.includes(tagId)))
            .modify((transaction) => {
              transaction.tagIds = transaction.tagIds.filter((tagId) => !tagIds.includes(tagId));
              transaction.updatedAt = now;
            });
          await db.tags.bulkDelete(tagIds);
        }

        await db.recurringExpenses
          .filter((recurringExpense) => recurringExpense.categoryId === id)
          .modify((recurringExpense) => {
            recurringExpense.categoryId = undefined;
            recurringExpense.updatedAt = now;
          });

        await db.categories.delete(id);
      });
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage('Impossible de supprimer la catégorie.', err));
    }
  }, []);

  const resetData = useCallback(async () => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }

    try {
      await db.categories.clear();
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage('Impossible de réinitialiser les catégories.', err));
    }
  }, []);

  return {
    categories,
    loading,
    error,
    addCategory,
    updateCategory,
    deleteCategory,
    resetData,
  };
}
